import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, Eraser, PenLine } from "lucide-react";
import api from "@/lib/api";

export const SIGNATURE_CLAUSE = "La firma apposta elettronicamente ha valore di sottoscrizione autografa ai sensi della normativa vigente.";

export const InternalSignaturePad = ({ token, contractId, signingToken, onSigned }) => {
    const padRef = useRef(null);
    const frameRef = useRef(null);
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const canvas = padRef.current?.getCanvas();
        const frame = frameRef.current;
        if (!canvas || !frame) return;
        const width = Math.max(280, Math.floor(frame.clientWidth));
        canvas.width = width;
        canvas.height = width < 480 ? 180 : 220;
        padRef.current.clear();
    }, []);

    const clear = () => {
        padRef.current?.clear();
        setError("");
    };

    const submit = async () => {
        if (!signingToken) {
            setError("Apri il link personale ricevuto via email per firmare questo documento.");
            return;
        }
        if (!padRef.current || padRef.current.isEmpty()) {
            setError("Apponi la firma nel riquadro prima di continuare.");
            return;
        }
        if (!consent) {
            setError("Devi accettare la clausola di sottoscrizione.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const response = await api.post(`/portal/${token}/contracts/${contractId}/sign`, {
                signature_data_url: padRef.current.getCanvas().toDataURL("image/png"),
                signing_token: signingToken,
                consent: true,
                consent_clause: SIGNATURE_CLAUSE,
            });
            onSigned?.(response.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Impossibile completare la firma. Riprova.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="internal-signature-panel" data-testid={`internal-signature-${contractId}`}>
            <div className="flex items-center gap-2 mb-3">
                <PenLine size={17} className="text-gold" />
                <h4 className="font-serif text-xl text-ink-50">Firma Digitale</h4>
            </div>
            <div ref={frameRef} className="signature-canvas-frame">
                <SignatureCanvas
                    ref={padRef}
                    penColor="#111111"
                    minWidth={1.2}
                    maxWidth={2.8}
                    clearOnResize={false}
                    canvasProps={{
                        className: "signature-canvas",
                        "data-testid": `signature-canvas-${contractId}`,
                        "aria-label": "Riquadro per apporre la firma grafica",
                    }}
                />
            </div>
            <button
                type="button"
                data-testid={`signature-clear-${contractId}`}
                onClick={clear}
                disabled={busy}
                className="signature-clear-btn"
            >
                <Eraser size={13} /> Cancella firma
            </button>
            <label className="signature-consent-row">
                <input
                    data-testid={`signature-consent-${contractId}`}
                    type="checkbox"
                    checked={consent}
                    onChange={(event) => setConsent(event.target.checked)}
                    disabled={busy}
                />
                <span>{SIGNATURE_CLAUSE}</span>
            </label>
            {!signingToken && (
                <p data-testid={`signature-link-required-${contractId}`} className="text-xs text-amber-300 mt-3">
                    Per firmare, apri il link personale ricevuto via email oppure richiedine uno nuovo all&apos;agenzia.
                </p>
            )}
            {error && <p data-testid={`signature-error-${contractId}`} className="text-xs text-red-300 mt-3">{error}</p>}
            <button
                type="button"
                data-testid={`signature-submit-${contractId}`}
                onClick={submit}
                disabled={busy || !signingToken}
                className="signature-submit-btn"
            >
                <CheckCircle2 size={15} /> {busy ? "Creazione PDF firmato…" : "Firma e salva nel CRM"}
            </button>
        </section>
    );
};